import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Report Service - Handles all operations related to user reports/flags
 * Allows users to report inappropriate content, scam campaigns, or abusive users.
 */
const reportService = {
  /**
   * Create a report
   * Prevents duplicate pending reports for the same target by the same reporter.
   * @param {string} reporterId - User ID of the reporter
   * @param {string} reportType - Type of report ('user', 'campaign', 'content', 'message')
   * @param {string} targetType - Type of target ('brand', 'influencer', 'campaign', 'proof', 'draft', 'message')
   * @param {string} targetId - ID of the reported target
   * @param {string} reason - Predefined category ('spam', 'harassment', 'scam', 'inappropriate_content', 'fake_followers', 'other')
   * @param {string} description - Detailed explanation of the report
   * @returns {Promise<object|null>} - Created report object with ID, or null if a duplicate pending report exists
   */
  createReport: async (reporterId, reportType, targetType, targetId, reason, description) => {
    try {
      const reportsRef = collection(db, 'reports');

      // Check for duplicate pending report by the same reporter for the same target
      const duplicateQuery = query(
        reportsRef,
        where('reporterId', '==', reporterId),
        where('targetId', '==', targetId),
        where('status', '==', 'pending')
      );

      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        return null; // Duplicate pending report exists, do not create another
      }

      const reportData = {
        reporterId,
        reportType,
        targetType,
        targetId,
        reason,
        description,
        status: 'pending',
        createdAt: serverTimestamp(),
        resolvedAt: null,
        resolvedBy: null,
        resolution: ''
      };

      const docRef = await addDoc(reportsRef, reportData);

      return { id: docRef.id, ...reportData };
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  },

  /**
   * Get a report by ID
   * @param {string} reportId - Report ID
   * @returns {Promise<object|null>} - Report object with ID, or null if not found
   */
  getReportById: async (reportId) => {
    try {
      const reportRef = doc(db, 'reports', reportId);
      const reportDoc = await getDoc(reportRef);

      if (!reportDoc.exists()) {
        return null;
      }

      return { id: reportDoc.id, ...reportDoc.data() };
    } catch (error) {
      console.error('Error getting report by ID:', error);
      throw error;
    }
  },

  /**
   * Get all reports submitted by a user
   * @param {string} reporterId - User ID of the reporter
   * @returns {Promise<Array>} - Array of report objects
   */
  getReportsByReporter: async (reporterId) => {
    try {
      const reportsRef = collection(db, 'reports');
      const q = query(
        reportsRef,
        where('reporterId', '==', reporterId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({ id: doc.id, ...doc.data() });
      });

      return reports;
    } catch (error) {
      console.error('Error getting reports by reporter:', error);
      throw error;
    }
  },

  /**
   * Get all reports for a specific target
   * @param {string} targetId - ID of the reported target
   * @returns {Promise<Array>} - Array of report objects
   */
  getReportsByTarget: async (targetId) => {
    try {
      const reportsRef = collection(db, 'reports');
      const q = query(
        reportsRef,
        where('targetId', '==', targetId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({ id: doc.id, ...doc.data() });
      });

      return reports;
    } catch (error) {
      console.error('Error getting reports by target:', error);
      throw error;
    }
  },

  /**
   * Get all reports with optional filters
   * @param {object} options - Query options (status, reportType, targetType)
   * @returns {Promise<Array>} - Array of report objects ordered by createdAt desc
   */
  getAllReports: async (options = {}) => {
    try {
      const reportsRef = collection(db, 'reports');

      // Build query
      let q = query(reportsRef, orderBy('createdAt', 'desc'));

      // Filter by status if specified
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }

      // Filter by reportType if specified
      if (options.reportType) {
        q = query(q, where('reportType', '==', options.reportType));
      }

      // Filter by targetType if specified
      if (options.targetType) {
        q = query(q, where('targetType', '==', options.targetType));
      }

      const querySnapshot = await getDocs(q);

      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({ id: doc.id, ...doc.data() });
      });

      return reports;
    } catch (error) {
      console.error('Error getting all reports:', error);
      throw error;
    }
  },

  /**
   * Update a report's status (resolve or dismiss)
   * @param {string} reportId - Report ID
   * @param {string} status - New status ('resolved' or 'dismissed')
   * @param {string} resolution - Resolution text explaining the outcome
   * @param {string} resolvedBy - Admin user ID who resolved the report
   * @returns {Promise} - Promise that resolves when the report is updated
   */
  updateReportStatus: async (reportId, status, resolution, resolvedBy) => {
    try {
      const reportRef = doc(db, 'reports', reportId);
      await updateDoc(reportRef, {
        status,
        resolution,
        resolvedBy,
        resolvedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  },

  /**
   * Get count of pending reports (for admin dashboard badge)
   * @returns {Promise<number>} - Number of pending reports
   */
  getPendingReportsCount: async () => {
    try {
      const reportsRef = collection(db, 'reports');
      const q = query(reportsRef, where('status', '==', 'pending'));

      const querySnapshot = await getDocs(q);

      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting pending reports count:', error);
      throw error;
    }
  },

  /**
   * Get report statistics
   * @returns {Promise<object>} - Object containing total, pending, resolved, dismissed counts and counts by reason
   */
  getReportStats: async () => {
    try {
      const reportsRef = collection(db, 'reports');
      const querySnapshot = await getDocs(reportsRef);

      const stats = {
        total: querySnapshot.size,
        pending: 0,
        resolved: 0,
        dismissed: 0,
        byReason: {}
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Count by status
        if (data.status === 'pending') {
          stats.pending++;
        } else if (data.status === 'resolved') {
          stats.resolved++;
        } else if (data.status === 'dismissed') {
          stats.dismissed++;
        }

        // Count by reason
        const reason = data.reason || 'other';
        stats.byReason[reason] = (stats.byReason[reason] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error getting report stats:', error);
      throw error;
    }
  }
};

export default reportService;
